/*
 *
 * Date: 2025
 * Description: Initial page for the application's first view
 * Author: Philippe Leroux @SKITSC
 *
 */


// Modules
import { Box, Typography , Grid, Paper, Button , Modal, CircularProgress } from "@mui/material";
import { ReactElement, useContext, useEffect , useState } from "react";
import { useNavigate } from "react-router-dom";

//Components
import Footer from "../components/utils/footer";
import FormBody from "../components/modal/form.body";

//Contexts
import { MainContext, ThemeContext } from "../context/context";

//Functions
import { handleFetch } from "../api/fetch";

//Interfaces
import { i_complete_list, i_list_status_history, i_question, i_statement } from "../interfaces/items.interface";
import { i_initial_props , i_modal_form_props } from "../interfaces/utility.interface";

//Constants
import {defaultQuestion } from "../utils/constant"
import { timestampToDate } from "../utils/utility";
import { t_list_status } from "../types/types";
import { useAlert } from "../context/alert.context";




const MyAccount = ( props : i_initial_props ) : ReactElement => {
    const { user , HandleLogout } = useContext(MainContext)
    const { mode } = useContext(ThemeContext)
    const [ data , setData ] = useState<i_statement[]>([])
    const [ order , setOrder ] = useState<i_complete_list[]>([])
    const [ form , setForm ] = useState<i_question>(defaultQuestion)
    const [ errors , setErrors ] = useState<i_question>(defaultQuestion)
    const [ loading , setLoading ] = useState<boolean>(false)
    const [ open , setOpen ] = useState<boolean>(false)
    const { showTopAlert } = useAlert();
    const nav = useNavigate();

    useEffect(() => {
        const getStatements = async() => {
            setLoading(true)
            const res = await handleFetch('/statements' , 'GET' , true , null)
            if(res.type === 'Success'){
                setData(res.data)
            }else{
                if(res.type === 'Unauthorized'){
                    HandleLogout(nav)
                }
                showTopAlert(res)
            }
            setLoading(false)
        }
        const getOrders = async() => {
            const orders = await handleFetch('/orders' , 'GET' , true , null)
            if(orders.type === 'Success'){
                setOrder(orders.data)
            }else{
                if(orders.type === 'Unauthorized'){
                    HandleLogout(nav)
                }
                showTopAlert(orders)
            }
        }
        getStatements()
        getOrders()
    },[])

    const RenderStatements = () : ReactElement => {
        return (
            <Box>
                {data.length > 0 ? 
                    <Box>

                    </Box>
                    :
                    <Box  sx={{ height: '160px', display: 'flex', justifyContent: 'center', alignItems: 'center', }}>
                        <Typography sx={{ fontSize : '20px' , textDecoration : 'underline'}}>No statements yet available.</Typography>
                    </Box>
                }
            </Box>
        )
    }
    const handleCallback = async() : Promise<void>  => {
        //Some validation here
        const res = await handleFetch('/question' , 'POST' , true , form)
        if(res.type === 'Success'){
            setForm(defaultQuestion)
            setErrors(defaultQuestion)
            setOpen(false)
        }else{
            if(res.type === 'Unauthorized'){
                HandleLogout(nav)
            }
            showTopAlert(res)
        }
    }
    const handleModalOpen = () : void => {
        setOpen(true)
    }
    const countTotal = ( items : i_list_status_history) => {
        return items.order.reduce((acc, curr) => acc + Number(curr.price), 0)
    }
    const renderStatus = ( status : t_list_status ) : ReactElement => {
        return (
            <Typography variant="body2" sx={{ color : status === 'completed' ? 'green' : status === 'transit' ? 'yellow' : 'red'}}>{status}</Typography>
        )
    }
    const countCredit = () => {
        var totalCredit = 0
        var totalPaid = 0;
        for(let i = 0 ; i < order.length ; i++){
            const status = order[i].status
            if(status === 'pending'){
                const total = countTotal(order[i].history[0])
                totalCredit += total
            }
            if(order[i].payments.length > 0){
                for(let j = 0 ; j < order[i].payments.length ; j++){
                    totalPaid += Number(order[i].payments[j].amount)
                }
            }
        }
        var total = totalCredit - totalPaid
        return total.toFixed(2)
    }
    const findLastPayment = (): string => {
        const defaultText: string = "No payments yet.";
        const allPayments = order.filter(o => o.status !== "completed").flatMap(o => o.payments);
        if (allPayments.length > 0) {
            const lastPayment = allPayments.reduce((latest, payment) =>
                payment.timestamp > latest.timestamp ? payment : latest
            );
            var time = timestampToDate(lastPayment.timestamp, "long")
            return time === 0 ? 'No payments yet.' : time === undefined ? 'No payments yet.' : time.toString();
        }
        return defaultText;
    };

const findLastPaymentAmount = (): string => {
    const defaultText: string = "0$";
    try {
        const allPayments = order.filter(o => o.status !== "completed").flatMap(o => o.payments); 
        if (allPayments.length > 0) {
            const lastPayment = allPayments.reduce((latest, payment) =>
                payment.timestamp > latest.timestamp ? payment : latest
            );
            return `${lastPayment.amount}$`;
        }
        return defaultText;
    } catch (err) {
        console.error(err);
        return "Error";
    }
};

    const renderAvailableCredit = () => {
        var totalCredit = countCredit()
        const total = user.creditLimit - Number(totalCredit)
        return total.toFixed(2)
    }
    const modalProps : i_modal_form_props = {
        open : open,
        title : 'Send us a message',
        form : form,
        select_arr : [],
        setForm: setForm,
        callback : handleCallback,
        handleClose : () => setOpen(false),
        loading : loading,
        mode : mode,
        errors : errors,
        type : 'question'
    }
    const RenderAddress = () : ReactElement => {
        let address : string = ''
        let secondLine : string = ''
        if(user.address.doorNumber !== ''){
            if(user.address.unitNumber !== '') address +=  user.address.unitNumber + '-'
            address +=  user.address.doorNumber + ', ' + user.address.street 
            secondLine +=  user.address.city + ', ' + user.address.state + ', ' + user.address.postalCode
        }
        return (
            <Box sx={{}}>
                {address !== '' &&
                    <>
                        <Typography >Address : {address}</Typography>
                        <Typography variant="body2">{secondLine}</Typography>
                    </>
                }
            </Box>
        )
    }
    return (
        <Box >
            <Box sx={{position: 'sticky', top: 0, height: '100%', overflowY: 'auto'}}>
            {loading ?
                <Box sx={{ display : 'flex' , justifyContent : 'center' , marginTop : '40vh'}}>
                    <CircularProgress size={100}/>
                </Box>
            :
            <Box>
            <Paper elevation={4} sx={{ maxWidth : '1400px' , marginLeft : 'auto' , marginRight : 'auto' , marginTop : '4vh' , padding : '24px'}}>
                <Grid container>
                    <Grid size={9}>
                        <Box sx={{ marginLeft : '20px', marginTop : '20px' }}>
                            <Typography variant="h5">My Account</Typography>
                            <Typography variant="body1">Welcome to your account. Here you can manage view your statements and recents purchases and events</Typography>

                            <Button variant={"contained"} sx={{ marginTop : '1vh' }} onClick={() => handleModalOpen()}>Have a question ?</Button>
                        </Box>
                    </Grid>
                    <Grid size={3}>
                        <Box sx={{ display : 'flex' , flexDirection : 'column'}}>
                            <Typography>My Details</Typography>
                            {user.businessName !== '' && <Typography>Business name : {user.businessName}</Typography> }
                            <RenderAddress />
                            <Typography>Phone number :{user.phone}</Typography>
                            <Typography>Email address :{user.email}</Typography>
                            {user.accountNumber !== '' && <Typography>Account number : {user.accountNumber}</Typography> }
                            <Typography>Last login : {timestampToDate(user.lastLogin  ,'long')}</Typography>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box sx={{ border : '1px solid gray' , margin : 4}}>
                            <Box sx={{ padding : '4px' , border : '1px solid gray'}}>
                                <Typography variant="h6" >Balance details</Typography>
                            </Box>
                            <Box>
                                <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography> Credit Limit :</Typography>
                                    <Typography>{user.creditLimit}$</Typography>
                                </Box>
                                <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography> Available Credit :</Typography>
                                    <Typography>{renderAvailableCredit()}$</Typography>
                                </Box>
                                <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography> Amount Due :</Typography>
                                    <Typography>{countCredit()}$</Typography>
                                </Box>
                                 <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography>Last payment:</Typography>
                                    <Typography>{findLastPayment()}</Typography>
                                </Box>
                                <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography>Last payment amount:</Typography>
                                    <Typography>{findLastPaymentAmount()}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box sx={{ border : '1px solid gray' , margin : 4}}>
                            <Box sx={{ padding : '4px' , border : '1px solid gray'}}>
                                <Typography variant="h6" >Statement details</Typography>
                            </Box>
                            <Box>
                               <RenderStatements />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid size={4}>
                        <Box sx={{ border : '1px solid gray' , margin : 4}}>
                            <Box sx={{ padding : '4px' , border : '1px solid gray'}}>
                                <Typography variant="h6" >Purchase Summary</Typography>
                            </Box>
                            <Box>
                                <Box sx={{ display : 'flex' , justifyContent : 'space-between' , padding : '4px'}}>
                                    <Typography> Credit Limit :</Typography>
                                    <Typography>{user.creditLimit}$</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

                <Paper elevation={4} sx={{ maxWidth : '1400px' , marginLeft : 'auto' , marginRight : 'auto' , marginTop : '6vh' , padding : '24px'}}>
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="h5">Latest events</Typography>
                        </Grid>
                        { order.length > 0 ?
                            <>
                            <Grid size={12}>
                                <Grid container sx={{ border : '1px solid gray' , padding :1}}>
                                    <Grid size={2}>
                                        <Typography variant="body1" >Order Number</Typography>
                                    </Grid>
                                    <Grid size={2}>
                                        <Typography variant="body1">Status</Typography>
                                    </Grid>
                                    <Grid size={3}>
                                        <Typography variant="body1">Date</Typography>
                                    </Grid>
                                    <Grid size={3}>
                                        <Typography variant="body1">Items</Typography>
                                    </Grid>
                                    <Grid size={2}>
                                        <Typography variant="body1">Total</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                                {order.map((item, index) => (
                                    <Grid key={index} size={12}>
                                        <Grid container sx={{ border : '1px solid gray' , padding :1}}>
                                            <Grid size={2}>
                                                <Typography variant="body1" >{item.order_number}</Typography>
                                            </Grid>
                                            <Grid size={2}>
                                                {renderStatus(item.status)}
                                            </Grid>
                                            <Grid size={3}>
                                                <Typography variant="body1">{timestampToDate(item.timestamp , 'long')}</Typography>
                                            </Grid>
                                            <Grid size={3}>
                                                 <Typography variant="body1">{item.items.length}</Typography>
                                            </Grid>
                                            <Grid size={2} sx={{}}>
                                                <Typography variant="body1" sx={{ marginRight : '14px'}}>{countTotal(item.history[0]).toFixed(2)} $</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                            </>
                    
                            :
                            <Typography variant="body1">Below are the latest events from your account. Purchase , returns and previous balance</Typography>
                        }

                    </Grid>
                </Paper>
                </Box>
                }
            </Box>            
            <Modal open={open} onClose={() => setOpen(false)} children={<FormBody {...modalProps}/>}/>
        </Box>
    )
}

export default MyAccount;